import { PrimaryButton } from '@artprov/design-system'
import PropTypes from 'prop-types'
import { useAlert } from '@common/components/alert/use-alert.hook'

/**
 * Convierte una ruta UNC (\\servidor\carpeta\archivo.xlsx) a una URL file:///
 * compatible con el navegador.
 *
 * @param {string} uncPath - Ruta UNC del archivo (ej: \\servidor\carpeta\archivo.xlsx)
 * @returns {string} URL con protocolo file:///
 *
 * @example
 * convertirUncAUrl('\\\\servidor\\carpeta\\archivo.xlsx')
 * // → 'file://///servidor/carpeta/archivo.xlsx'
 */
const convertirUncAUrl = (uncPath) => {
  if (!uncPath) return ''

  // Reemplazar backslashes con forward slashes
  let url = uncPath.replace(/\\/g, '/')

  // Si comienza con // (UNC path), agregar file:/// (total 5 slashes para UNC)
  // Formato correcto: file://///servidor/carpeta/archivo.xlsx
  if (url.startsWith('//')) {
    url = `file:///${url}` // file:/// + //servidor = file://///servidor
  } else if (url.startsWith('/')) {
    url = `file://${url}`
  } else {
    url = `file:///${url}`
  }
  console.log('URL', url )
  return url
}

/**
 * Componente botón que intenta abrir un archivo Excel desde una ruta UNC
 * usando el protocolo file:///.
 *
 * IMPORTANTE:
 * - Funciona solo si la aplicación está configurada como sitio de intranet
 *   y el navegador permite navegación a file:/// desde el origen.
 * - En Chrome/Edge modernos, requiere configuración adicional (ver documentación).
 *
 * @param {Object} props
 * @param {string} props.uncPath - Ruta UNC del archivo Excel
 * @param {string} [props.label='Abrir planilla Excel'] - Texto del botón
 * @param {boolean} [props.disabled=false] - Deshabilitar botón
 * @param {'small'|'medium'} [props.size='small'] - Tamaño del botón
 */
export default function AbrirExcelButton({
  uncPath,
  label = 'Abrir planilla Excel',
  disabled = false,
  size = 'small',
  ...props
}) {
  const alert = useAlert()

  const handleClick = () => {
    if (!uncPath) {
      alert.show({
        description: 'No se ha configurado la ruta del archivo Excel.',
        variant: 'warning',
        autoDismiss: true,
      })
      return
    }

    const fileUrl = convertirUncAUrl(uncPath)

    try {
      // Método 1: Intentar con protocolo ms-excel (funciona mejor en Windows)
      const excelProtocolUrl = `ms-excel:ofe|u|${fileUrl}`
      
      // Crear iframe oculto para intentar abrir con protocolo de Excel
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = excelProtocolUrl
      document.body.appendChild(iframe)
      
      // Remover el iframe después de un momento
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)

      // Método 2: Fallback con window.location (si el protocolo ms-excel no funciona)
      setTimeout(() => {
        try {
          window.location.href = fileUrl
        } catch (e) {
          console.warn('Fallback method failed:', e)
        }
      }, 500)

      // Mostrar mensaje informativo
      // alert.show({
      //   description:
      //     'Si el archivo no se abrió automáticamente, verificá que Microsoft Excel esté instalado y que tengas acceso a la ruta de red.',
      //   variant: 'info',
      //   autoDismiss: false,
      // })
    } catch (error) {
      console.log('ERROR', error)
      alert.show({
        description: 'No se pudo abrir el archivo. Verificá la conexión a la red corporativa y que Excel esté instalado.',
        variant: 'warning',
        autoDismiss: false,
      })
    }
  }

  return (
    <PrimaryButton IconLeft="description" size={size} onClick={handleClick} disabled={disabled || !uncPath} {...props}>
      {label}
    </PrimaryButton>
  )
}

AbrirExcelButton.propTypes = {
  uncPath: PropTypes.string.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
}
